import Panel from "ui/Panel";
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { WorkbookDetailsDocument } from "../Workbook/gql/Query.generated";
import { useUpdateWorkbookParameterSetsMutation } from "./gql/Mutation.update.generated";
import {
  WorkbookType,
  WorkbookVariantType,
  AliasParamMapType,
} from "../Workbook";

import {
  ParameterSetsType,
  ParamSetType,
  stringifyValue,
  toParamSet,
  toParameterSetPayload,
} from "../Workbook/util";
import { useParamSetInputs } from "../ParameterSetInputs";
import Callout from "ui/Callout";
import {
  faCheckCircle,
  faCog,
  faExclamationCircle,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { useNavigate, useParams } from "react-router-dom";
import { useRoutes } from "utils/routes";
import { VariantPanel } from "../WorkbookVariant";
import PanelSection from "ui/PanelSection";
import PageContent from "ui/PageContent";
import {
  CreateVariantSteps,
  VariantCreationHeaderWithVariant,
} from "../RewriteQuery";
import Button from "ui/Button";
import EditParameterSettingsPanel from "../EditParameterSettingsPanel";

// Shown in place of the Run EXPLAIN page after a variant introduces new parameters,
// until every existing parameter set has a value for them. Collects the missing
// values for every parameter set, then continues to the Run EXPLAIN page.
const FillMissingParameterValues = ({
  workbook,
  variant,
}: {
  workbook: WorkbookType;
  variant: WorkbookVariantType;
}) => {
  const { databaseId } = useParams();
  const [errorMessage, setErrorMessage] = useState("");
  const [showParameterSettingsEditPanel, setShowParameterSettingsEditPanel] =
    useState(false);
  const { databaseWorkbookVariantRun } = useRoutes();
  const navigate = useNavigate();
  // key = setId, value = param set (params to newly add)
  const [newParameterSets, setNewParameterSets] = useState<ParameterSetsType>(
    {},
  );

  const [updateWorkbookParameterSets] = useUpdateWorkbookParameterSetsMutation({
    refetchQueries: [
      {
        query: WorkbookDetailsDocument,
        variables: { workbookId: workbook.id, databaseId },
      },
    ],
  });

  const expectedAliasesLength = workbook.parameterRefAliases.length;
  // For the case the workbook was initially created without params but new param set is added
  const newParamNotFilled =
    workbook.parameterSets.length === 0 && expectedAliasesLength > 0;
  const missingAliasCount = newParamNotFilled
    ? expectedAliasesLength
    : expectedAliasesLength - workbook.parameterSets[0].paramValues.length;
  const allValuesSet =
    !newParamNotFilled &&
    Object.keys(newParameterSets).length === workbook.parameterSets.length &&
    Object.values(newParameterSets).every(
      (paramSet) => Object.keys(paramSet).length === missingAliasCount,
    );

  const handleRunExplain = () => {
    if (!allValuesSet) {
      setErrorMessage(
        "Please enter values, types, or NULL flags for all parameters.",
      );
      return;
    }
    const convertedParameterSets = Object.fromEntries(
      Object.entries(newParameterSets).map(([setId, paramSet]) => {
        return [setId, toParameterSetPayload(toParamSet(paramSet, workbook))];
      }),
    );
    updateWorkbookParameterSets({
      variables: {
        workbookId: workbook.id,
        parameterSets: convertedParameterSets,
      },
      onCompleted: () => {
        navigate(
          databaseWorkbookVariantRun(
            workbook.databaseId,
            workbook.id,
            variant.id,
          ),
        );
      },
      onError: (error) => {
        setErrorMessage(error.message);
      },
    });
  };
  const title = <VariantCreationHeaderWithVariant variant={variant} />;
  const editParameterSettings = (
    <Button
      bare
      onClick={() => setShowParameterSettingsEditPanel(true)}
      variant="link"
    >
      <FontAwesomeIcon icon={faCog} className="mr-0.5" /> Edit parameter
      settings
    </Button>
  );

  // TODO: show component to allow creating a new param set when newParamNotFilled
  return (
    <PageContent
      windowTitle={`EXPLAIN Workbook: ${workbook.name}`}
      title={title}
      pageCategory="workbooks"
      pageName="fill-missing-parameter-values"
    >
      <Panel
        title={<CreateVariantSteps step="step1" />}
        secondaryTitle={editParameterSettings}
      >
        <PanelSection className="grid gap-2">
          <VariantPanel workbook={workbook} variant={variant} />
          <Callout title="Parameter Sets need an update" variant="warning">
            New parameters are added with this new variant. Please update
            parameter set values.
          </Callout>
          <div>
            {workbook.aliasParamMapList.map((val) => {
              return (
                <PanelSection key={val.id}>
                  <ParameterSetCell
                    currParameterSet={val}
                    workbook={workbook}
                    setNewParameterSets={setNewParameterSets}
                  />
                </PanelSection>
              );
            })}
          </div>
          {errorMessage && (
            <div className="text-[#C22426]">
              <FontAwesomeIcon icon={faExclamationCircle} /> {errorMessage}
            </div>
          )}
          <div>
            <button
              className="btn btn-success !px-4"
              disabled={!allValuesSet}
              onClick={handleRunExplain}
            >
              Run EXPLAIN...
            </button>
          </div>
        </PanelSection>
      </Panel>
      {showParameterSettingsEditPanel && (
        <EditParameterSettingsPanel
          onDismiss={() => setShowParameterSettingsEditPanel(false)}
          workbook={workbook}
          showExistingParameterWarning
        />
      )}
    </PageContent>
  );
};

const ParameterSetCell = ({
  currParameterSet,
  workbook,
  setNewParameterSets,
}: {
  currParameterSet: AliasParamMapType;
  workbook: WorkbookType;
  setNewParameterSets: React.Dispatch<React.SetStateAction<ParameterSetsType>>;
}) => {
  const missingAliases = missingValueAliases(workbook, currParameterSet.id);

  const { paramSet, handleParamValueChange, handleParamNullChange } =
    useParamSetInputs(workbook, {}, (newSet) =>
      setNewParameterSets((prevState) => {
        return {
          ...prevState,
          [currParameterSet.id]: newSet,
        };
      }),
    );

  const inputFields: React.JSX.Element[] = [];
  missingAliases.forEach((alias) => {
    const aliasStr = `$${alias}`;
    inputFields.push(
      <div key={`label-${alias}`} className="font-medium">
        {aliasStr}
      </div>,
    );
    inputFields.push(
      <input
        key={`input-${alias}`}
        className="bg-white rounded border border-gray-300 box-content h-5 leading-5 px-2 py-1.5 disabled:bg-[#eee]"
        type="text"
        value={paramSet[aliasStr]?.value ?? ""}
        disabled={paramSet[aliasStr]?.isNull}
        onChange={(e) => {
          handleParamValueChange(aliasStr, e.target.value);
        }}
      />,
    );
    inputFields.push(
      <div key={`null-${alias}`}>
        NULL:{" "}
        <input
          type="checkbox"
          checked={paramSet[aliasStr]?.isNull || false}
          onChange={(e) => handleParamNullChange(aliasStr, e.target.checked)}
        />
      </div>,
    );
  });
  return (
    <div>
      <div>
        <div className="flex gap-2 items-center">
          {missingAliases.length === 0 ? (
            <FontAwesomeIcon icon={faCheckCircle} />
          ) : (
            <FontAwesomeIcon icon={faTriangleExclamation} />
          )}
          <div className="font-medium">{currParameterSet.name}</div>
        </div>
        <div>
          <pre className="border-none m-0 p-0 bg-none bg-transparent whitespace-pre-wrap">
            {Object.entries(currParameterSet.parameters as ParamSetType).map(
              ([key, value], i, arr) => {
                const missing = missingAliases.includes(key.slice(1));
                const missingClass = missing ? "text-[#A16006]" : "";
                return (
                  <React.Fragment key={key}>
                    <span className={missingClass}>
                      {missing
                        ? key
                        : `${key} = ${stringifyValue(value.value)}`}
                    </span>
                    {i + 1 < arr.length && ", "}
                  </React.Fragment>
                );
              },
            )}
          </pre>
        </div>
      </div>
      {missingAliases.length > 0 && (
        <div>
          <div className="grid grid-cols-[min-content_1fr_80px] gap-2 mb-2 mt-2 items-center">
            {inputFields}
          </div>
        </div>
      )}
    </div>
  );
};

export const missingValueAliases = (workbook: WorkbookType, setId: string) => {
  const existingParam = workbook.parameterSets.find((val) => val.id === setId);
  const expectedAliases = workbook.parameterRefAliases;
  return expectedAliases.slice(existingParam.paramValues.length);
};

export default FillMissingParameterValues;
